import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Typography, 
    Button, 
    Grid, 
    Paper, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow,
    TextField,
    IconButton,
    Box,
    Divider,
    Chip,
    Card,
    CardContent,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    Tooltip,
    useTheme,
    Avatar,
    Badge
} from '@mui/material';
import { 
    Add, 
    Remove, 
    Delete, 
    ShoppingCart, 
    Search, 
    Person,
    Phone, 
    Money, 
    Close, 
    Category as CategoryIcon, 
    Close as CloseIcon, 
    PersonAdd,
    MergeType,
    History
} from '@mui/icons-material';
import useCustomToast from '../../../hooks/useToast';
import axios from 'axios';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../../services/Text';
import { 
    APCustomerTypeNames,
    APICounterNames,
    APIPositionNames,
    APIEmployeeNames,
    APIClubOrderStore,
    APIClubOrderPos,
    APIClubOrderPending
} from '../../../services/APIs';
import { 
    formatNumberWithComma, 
    getCategoriesNameById,
    getUserDetails,
    getUserRole,
} from '../../../utils/helpers';
import PrintReceipt from './PrintReceipt';
import { PERMISSIONS, ROLE_PERMISSIONS } from '../../../utils/Permission';
import { apiService } from '../../../apiServices/ApiService';
import { useNavigate, useLocation } from 'react-router-dom';

const ClubPos = () => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [processingOrder, setProcessingOrder] = useState(false);
    const [error, setError] = useState(null);
    const theme = useTheme();
    const showToast = useCustomToast();

    const [pageState, setPageState] = useState({
        stockData: [],
        pendingOrders: []
    });

    const [cart, setCart] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedBranch, setSelectedBranch] = useState('');
    const [showPendingOrders, setShowPendingOrders] = useState(false);
    const [selectedPendingOrder, setSelectedPendingOrder] = useState(null);

    // Form states
    const [formData, setFormData] = useState({
        customer_id: '',
        customer_type_id: '',
        employee_position_id: '',
        servant_id: '',
        previousOrderNumber: '',
        counter_id: getUserDetails("counterId"),
        order_notes: '', 
        parent_order_id: ''
    });

    const [referenceData, setReferenceData] = useState({
        branchNames: [],
        customerType: [],
        counterNames: [],
        employeeNames: [],
        positionNames: []
    });

    const [orderSettingsTab, setOrderSettingsTab] = useState(0);
    const [customerEditable, setCustomerEditable] = useState(true);

    // Permissions
    const [userRole, setUserRole] = useState('');

    useEffect(() => {
        const role = getUserRole();
        setUserRole(role);
    }, []);

    useEffect(() => {
        apiService.setToastFunction(showToast);
    }, [showToast]);

    const hasPermission = (permission) => {
        return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
    };

    useEffect(() => {
        fetchData();
        fetchReferenceData();
    }, [selectedBranch]);

    // Get customer data from navigation state
    useEffect(() => {
        if (location.state) {
            const { customerId, customerTypeId, previousOrderNumber, counterId } = location.state;
            if (customerId) {
                setFormData(prev => ({
                    ...prev,
                    customer_id: customerId,
                    customer_type_id: customerTypeId || '',
                    previousOrderNumber: previousOrderNumber || '',
                    counter_id: counterId || getUserDetails("counterId")
                }));
                setCustomerEditable(false);
                
                // Fetch pending orders for this customer
                fetchCustomerPendingOrders(customerId);
                
                showToast("Info", `Adding order for customer: ${customerId}`, "info");
            }
        }
    }, [location]);

    const fetchCustomerPendingOrders = async (customerId) => {
        try {
            const response = await axios.get(APIClubOrderPending, {
                params: { customer_id: customerId }
            });
            
            if (response.data.status === 'success') {
                setPageState(prev => ({
                    ...prev,
                    pendingOrders: response.data.data
                }));
                
                if (response.data.data.length > 0) {
                    setShowPendingOrders(true);
                }
            }
        } catch (error) {
            console.error('Error fetching pending orders:', error);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            // Fetch stock items
            const params = {};
            params.counter_id = getUserDetails("counterId");

            const stockRes = await axios.get(APIClubOrderPos, { params });

            // Process data
            const stockItems = Array.isArray(stockRes.data.data)
                ? stockRes.data.data
                    .filter(item => item.status === 1 && item.balance > 0)
                    .reverse() 
                : [];

            setPageState(prev => ({
                ...prev,
                stockData: stockItems,
            }));

            showToast("Success", TEXT_DATA_LOADED, "success");
        } catch (error) {
            let errorMessage = "Failed to load data";
            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }
            setError(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const fetchReferenceData = async () => {
        try {
            // Fetch all reference data in parallel
            const [branchesResult, empResult, posResult, cotResult, typResult] = await Promise.all([
                apiService.branchNames(),
                apiService.listNames(APIEmployeeNames),
                apiService.listNames(APIPositionNames),
                apiService.listNames(APICounterNames),
                apiService.listNames(APCustomerTypeNames),
            ]);

            setReferenceData({
                branchNames: branchesResult.success ? branchesResult.data : [],
                employeeNames: empResult.success ? empResult.data : [],
                positionNames: posResult.success ? posResult.data : [],
                counterNames: cotResult.success ? cotResult.data : [],
                customerType: typResult.success ? typResult.data : [],
            });
        } catch (error) {
            showToast("Error", "Failed to load reference data", "error");
        }
    };

    // Filter stock items based on search
    const filteredStockItems = pageState.stockData.filter(item => {
        const matchesSearch = searchTerm === '' || 
            item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.description?.toLowerCase().includes(searchTerm.toLowerCase());
        
        return matchesSearch;
    });

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Cart handlers
    const addToCart = (item) => {
        const existingItem = cart.find(cartItem => cartItem.id === item.id);
        if (existingItem) {
            if (existingItem.quantity < item.balance) {
                setCart(cart.map(cartItem =>
                    cartItem.id === item.id
                        ? { ...cartItem, quantity: cartItem.quantity + 1 }
                        : cartItem
                ));
            } else {
                showToast("Stock Limit", "Cannot add more than available quantity", "warning");
            }
        } else {
            setCart([...cart, { 
                ...item, 
                quantity: 1,
                price: item.selling || 0
            }]);
        }
    };

    const removeFromCart = (itemId) => {
        setCart(cart.filter(item => item.id !== itemId));
    };

    const adjustQuantity = (itemId, adjustment) => {
        setCart(cart.map(item => {
            if (item.id === itemId) {
                const newQuantity = item.quantity + adjustment;
                const stockItem = pageState.stockData.find(s => s.id === itemId);
                if (newQuantity > 0 && newQuantity <= stockItem.balance) {
                    return { ...item, quantity: newQuantity };
                } else if (newQuantity > stockItem.balance) {
                    showToast("Stock Limit", "Cannot add more than available quantity", "warning");
                }
            }
            return item;
        }));
    };

    const calculateTotal = () => {
        return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
    };

    const calculateCombinedTotal = (newTotal, selectedTotal) => {
        return newTotal + selectedTotal;
    };

    const validateForm = () => {
        const errors = [];
        if (cart.length === 0) {
            errors.push("Cart is empty");
        }
        if (!formData.servant_id) {
            errors.push("Servant is required");
        }
        if (!formData.counter_id) {
            errors.push("Counter is required");
        }
        if (!formData.customer_id) {
            errors.push("Customer is required");
        }

        if (errors.length > 0) {
            showToast("Validation Error", errors.join('\n'), "warning");
            return false;
        }
        return true;
    };

    const handleCombineOrder = (pendingOrder) => {
        setSelectedPendingOrder(pendingOrder);
        setFormData(prev => ({
            ...prev,
            previousOrderNumber: pendingOrder.order_number
        }));
        setShowPendingOrders(false);
        
        showToast(
            "Info", 
            `Adding items to existing order #${pendingOrder.order_number}. Current total: ${formatNumberWithComma(pendingOrder.total_amount)}`, 
            "info"
        );
    };

    const handleCheckout = async () => {
        if (!validateForm()) return;

        setProcessingOrder(true);
        
        // Prepare order data
        const orderData = {
            user_id: getUserDetails("Id"),
            customer_id: formData.customer_id,
            customer_type_id: formData.customer_type_id,
            servant_id: formData.servant_id,
            counter_id: formData.counter_id,
            order_notes: formData.order_notes,
            previousOrderNumber: formData.previousOrderNumber,
            order_status: 'pending',
            total_amount: calculateTotal(),
            items: cart.map(item => ({
                stock_id: item.id,
                quantity: item.quantity,
                price: item.price,
                total: item.price * item.quantity
            })),
            branch_id: getUserDetails("branchId"),
            parent_order_id: formData.parent_order_id
        };

        try {
            const response = await axios.post(APIClubOrderStore, orderData);

            if (response.data.status === "success") {
                // Reset cart and form
                setCart([]);
                setFormData({
                    customer_id: '',
                    customer_type_id: '',
                    employee_position_id: '',
                    servant_id: '',
                    counter_id: getUserDetails("counterId"),
                    order_notes: '',
                    previousOrderNumber: '',
                    parent_order_id: ''
                });
                setCustomerEditable(true);
                setSelectedPendingOrder(null);

                const message = response.data.is_combined 
                    ? `Items added to existing order #${response.data.order_number}`
                    : "Club order completed successfully!";
                
                showToast("Success", message, "success");
                
                // Refresh stock data
                fetchData();
                
                // Navigate back to order list if this was a new order from existing customer
                if (formData.parent_order_id) {
                    navigate('/club/orders');
                }
            } else {
                showToast("Error", response.data.message || "Failed to complete order", "error");
            }
        } catch (error) {
            let errorMessage = "Failed to complete order";
            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }
            showToast("Error", errorMessage, "error");
        } finally {
            setProcessingOrder(false);
        }
    };

    const handleClearAllFilters = () => {
        setSearchTerm('');
    };
    
    const handleOrderSettingsTabChange = (event, newValue) => {
        setOrderSettingsTab(newValue);
    };
    
    const getInitials = (name) => {
        if (!name) return '??';
        return name
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
    };

    if (loading) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
        </Box>
    );

    if (error) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <Typography color="error">Error: {error}</Typography>
        </Box>
    );

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ShoppingCart color="primary" sx={{ fontSize: 20, mr: 2 }} />
                    <Typography sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        Club Point of Sale
                    </Typography>
                </Box>
                
                {formData.previousOrderNumber && (
                    <Chip
                        icon={<MergeType />}
                        label={`Combining with order #${formData.previousOrderNumber}`}
                        color="info"
                        onDelete={() => {
                            setFormData(prev => ({ ...prev, previousOrderNumber: '' }));
                            setSelectedPendingOrder(null);
                        }}
                    />
                )}
            </Box>

            {/* Pending Orders Dialog - Updated with BarPos.js logic */}
            <Dialog 
                open={showPendingOrders} 
                onClose={() => setShowPendingOrders(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center">
                            <History sx={{ mr: 1 }} />
                            Pending Orders for this Customer
                        </Box>
                        <IconButton onClick={() => setShowPendingOrders(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        You have pending orders for this customer. You can add items to an existing order or create a new one.
                    </Typography>
                    
                    <Grid container spacing={2}>
                        {pageState.pendingOrders.map((order) => (
                            <Grid item xs={12} key={order.id}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} md={4}>
                                                <Typography variant="subtitle2">
                                                    Order #{order.order_number}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {new Date(order.created_at).toLocaleString()}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                {/* FIXED: Check if club_order_items is array, don't parse it */}
                                                <Typography variant="body2">
                                                    Items: {Array.isArray(order.club_order_items) ? order.club_order_items.length : 0}
                                                </Typography>
                                                <Typography variant="body2">
                                                    Total: {formatNumberWithComma(order.total_amount)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => handleCombineOrder(order)}
                                                    startIcon={<MergeType />}
                                                >
                                                    Add to this Order
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                        
                        <Grid item xs={12}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => setShowPendingOrders(false)}
                            >
                                Create New Order Instead
                            </Button>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>

            <Grid container spacing={3} sx={{ display: 'flex', flexWrap: 'nowrap' }}>
                {/* Stock Items List - Left Side */}
                <Grid item xs={12} md={8} sx={{ flex: '0 0 60%' }}>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: '100%', background: '#F5F5F0' }}>
                        {/* Search Section */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12}>
                                <Paper sx={{
                                    p: '2px 8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderRadius: '8px',
                                    backgroundColor: theme.palette.primary.light
                                }}>
                                    <Search sx={{ mr: 1, color: 'white' }} />
                                    <TextField
                                        fullWidth
                                        variant="standard"
                                        placeholder="Search Stock Items ..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        sx={{
                                            '& .MuiInput-underline:before': { borderBottom: 'none' },
                                            '& .MuiInput-underline:after': { borderBottom: 'none' },
                                            fontSize: '0.875rem',
                                            color: 'white',
                                            '& input': {
                                                color: 'white',
                                                '&::placeholder': {
                                                    color: 'rgba(255, 255, 255, 0.7)',
                                                    opacity: 1
                                                }
                                            }
                                        }}
                                        InputProps={{
                                            endAdornment: searchTerm && (
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => setSearchTerm('')}
                                                    sx={{ color: 'white' }}
                                                >
                                                    <Close fontSize="small" />
                                                </IconButton>
                                            )
                                        }}
                                        size="small"
                                    />
                                </Paper>
                                
                                {/* Clear All Filters Button */}
                                {searchTerm && (
                                    <Button
                                        variant="outlined"
                                        startIcon={<Close />}
                                        onClick={handleClearAllFilters}
                                        size="small"
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            mt: 1
                                        }}
                                    >
                                        Clear Search
                                    </Button>
                                )}
                            </Grid>
                        </Grid>

                        {/* Stock Items Grid */}
                        <Grid container spacing={2}>
                            {filteredStockItems.map((item) => (
                                <Grid item xs={6} sm={4} md={3} key={item.id}>
                                    <Card
                                        elevation={1}
                                        sx={{
                                            cursor: 'pointer',
                                            borderRadius: 2,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                backgroundColor: 'primary.light',
                                                color: 'white',
                                                transform: 'translateY(-2px)',
                                                boxShadow: 3
                                            }
                                        }}
                                        onClick={() => addToCart(item)}
                                    >
                                        <CardContent sx={{ p: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <Avatar
                                                    sx={{ 
                                                        width: 60, 
                                                        height: 60, 
                                                        bgcolor: 'primary.main',
                                                        fontSize: '18px',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {getInitials(item.name)}
                                                </Avatar>
                                                <Box sx={{ ml: 1.5 }}>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {item.uom}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                {item.name}
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    {formatNumberWithComma(item.selling || 0)}
                                                </Typography>
                                                <Chip
                                                    label={`Available: ${item.balance}`}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: item.balance > 20 ? 'success.light' : 
                                                                    item.balance > 5 ? 'warning.light' : 'error.light',
                                                        color: 'white',
                                                        alignSelf: 'flex-start',
                                                        fontSize: '0.7rem'
                                                    }}
                                                />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}

                            {filteredStockItems.length === 0 && (
                                <Grid item xs={12}>
                                    <Box sx={{
                                        textAlign: 'center',
                                        py: 6,
                                        border: `1px dashed ${theme.palette.divider}`,
                                        borderRadius: 2
                                    }}>
                                        <Search sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                        <Typography color="text.secondary" gutterBottom>
                                            No stock items found
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {searchTerm ? 
                                                "Try adjusting your search criteria" : 
                                                "No stock items available"}
                                        </Typography>
                                        {searchTerm && (
                                            <Button
                                                variant="outlined"
                                                startIcon={<Close />}
                                                onClick={handleClearAllFilters}
                                                size="small"
                                                sx={{ mt: 2 }}
                                            >
                                                Clear Search
                                            </Button>
                                        )}
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                </Grid>

                {/* Order Summary and Settings - Right Side */}
                <Grid item xs={12} md={8} sx={{ flex: '0 0 40%' }}>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                        <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
                            <ShoppingCart sx={{ verticalAlign: 'middle', mr: 1 }} />
                            Order Summary
                            {selectedPendingOrder && (
                                <Chip 
                                    size="small"
                                    label={`Combining with #${selectedPendingOrder.order_number}`}
                                    color="info"
                                    sx={{ ml: 1 }}
                                />
                            )}
                        </Typography>

                        {/* Tabs for Order Settings */}
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                            <Tabs 
                                value={orderSettingsTab} 
                                onChange={handleOrderSettingsTabChange}
                                variant="fullWidth"
                            >
                                <Tab label="Customer Info" />
                                <Tab label="Order Settings" />
                                <Tab label={
                                    <Badge badgeContent={cart.length} color="primary">
                                        Cart & Payment
                                    </Badge>
                                } />
                            </Tabs>
                        </Box>

                        {/* Customer Info Tab */}
                        {orderSettingsTab === 0 && (
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, position: 'relative' }}>
                                    <Person color="action" sx={{ mr: 1 }} />
                                    <TextField
                                        fullWidth
                                        label="Customer Name"
                                        variant="outlined"
                                        value={formData.customer_id}
                                        name="customer_id"
                                        onChange={handleInputChange}
                                        size="small"
                                    />
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <CategoryIcon color="action" sx={{ mr: 1 }} />
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Customer Type</InputLabel>
                                            <Select
                                                value={formData.customer_type_id}
                                                onChange={handleInputChange}
                                                name="customer_type_id"
                                                label="Customer Type"
                                            >
                                                <MenuItem value="">
                                                    <em>Select Customer Type</em>
                                                </MenuItem>
                                                {referenceData.customerType.map((type) => (
                                                    <MenuItem key={type.id} value={type.id}>
                                                        {type.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Order Settings Tab */}
                        {orderSettingsTab === 1 && (
                            <Box>
                                <Box>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Employee Position *</InputLabel>
                                            <Select
                                                name="employee_position_id"
                                                value={formData.employee_position_id || ''}
                                                onChange={(e) => {
                                                    const positionId = e.target.value;
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        employee_position_id: positionId,
                                                        servant_id: '' // Clear servant selection when position changes
                                                    }));
                                                }}
                                                label="Employee Position *"
                                                required
                                            >
                                                <MenuItem value="">
                                                    <em>Select employee position</em>
                                                </MenuItem>
                                                {referenceData.positionNames.map((position) => (
                                                    <MenuItem key={position.id} value={position.id}>
                                                        {position.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <br/>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Servant *</InputLabel>
                                            <Select
                                                name="servant_id"
                                                value={formData.servant_id}
                                                onChange={handleInputChange}
                                                label="Servant *"
                                                required
                                                disabled={!formData.employee_position_id}
                                            >
                                                <MenuItem value="">
                                                    <em>
                                                        {formData.employee_position_id
                                                        ? 'Select a Servant'
                                                        : 'Please select position first'}
                                                    </em>
                                                </MenuItem>
                                                {referenceData.employeeNames
                                                    .filter(employee =>
                                                        formData.employee_position_id
                                                        ? employee.position_id === formData.employee_position_id
                                                        : true
                                                    )
                                                    .map((employee) => (
                                                        <MenuItem key={employee.id} value={employee.id}>
                                                            {employee.employee_name || employee.name}
                                                        </MenuItem>
                                                    ))
                                                }
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <br/>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Counter Name"
                                            variant="outlined"
                                            value={getCategoriesNameById(formData.counter_id, referenceData.counterNames)}
                                            size="small"
                                            disabled
                                        />
                                    </Grid>
                                    <br/>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Order Notes (Optional)"
                                            variant="outlined"
                                            value={formData.order_notes}
                                            name="order_notes"
                                            onChange={handleInputChange}
                                            multiline
                                            rows={3}
                                            size="small"
                                        />
                                    </Grid>
                                </Box>
                            </Box>
                        )}

                        {/* Cart & Payment Tab */}
                        {orderSettingsTab === 2 && (
                            <Box>
                                {/* Cart Items */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography sx={{ mb: 2, fontWeight: 'bold' }}>
                                        Cart Items ({cart.length})
                                    </Typography>
                                    <TableContainer sx={{ maxHeight: 200 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                                                    <TableCell sx={{ color: 'white' }}>Item</TableCell>
                                                    <TableCell align="right" sx={{ color: 'white' }}>Qty</TableCell>
                                                    <TableCell align="right" sx={{ color: 'white' }}>Price</TableCell>
                                                    <TableCell align="right" sx={{ color: 'white' }}>Total</TableCell>
                                                    <TableCell align="right" sx={{ color: 'white' }}>Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {cart.map((item) => (
                                                    <TableRow key={item.id} hover>
                                                        <TableCell>{item.name}</TableCell>
                                                        <TableCell align="right">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => adjustQuantity(item.id, -1)}
                                                                disabled={item.quantity <= 1}
                                                                color="secondary"
                                                            >
                                                                <Remove fontSize="small" />
                                                            </IconButton>
                                                            <span style={{ minWidth: '20px', display: 'inline-block' }}>{item.quantity}</span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => adjustQuantity(item.id, 1)}
                                                                disabled={item.quantity >= pageState.stockData.find(s => s.id === item.id)?.balance}
                                                                color="secondary"
                                                            >
                                                                <Add fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                        <TableCell align="right">{formatNumberWithComma(item.price)}</TableCell>
                                                        <TableCell align="right">{formatNumberWithComma(item.price * item.quantity)}</TableCell>
                                                        <TableCell align="right">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => removeFromCart(item.id)}
                                                                color="error"
                                                            >
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    
                                    {cart.length === 0 && (
                                        <Box sx={{ textAlign: 'center', py: 3 }}>
                                            <Typography variant="body2" color="textSecondary">
                                                Your cart is empty. Add stock items to continue.
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                {/* Payment Section - Updated with calculateCombinedTotal */}
                                <Box sx={{ mb: 3 }}>
                                    <Box sx={{
                                        backgroundColor: 'primary.light',
                                        p: 2,
                                        borderRadius: 2,
                                        mb: 2
                                    }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontWeight: 'bold', color: 'white' }}>Total Amount:</Typography>
                                            <Typography sx={{ fontWeight: 'bold', color: 'white' }}>
                                                {formatNumberWithComma(calculateTotal())}
                                            </Typography>
                                        </Box>
                                        
                                        {selectedPendingOrder && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                                <Typography sx={{ color: 'white', fontSize: '0.875rem' }}>
                                                    Existing Order Total:
                                                </Typography>
                                                <Typography sx={{ color: 'white', fontSize: '0.875rem' }}>
                                                    {formatNumberWithComma(selectedPendingOrder.total_amount)}
                                                </Typography>
                                            </Box>
                                        )}
                                        
                                        {selectedPendingOrder && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                                <Typography sx={{ color: 'white', fontWeight: 'bold' }}>
                                                    Combined Total:
                                                </Typography>
                                                <Typography sx={{ color: 'white', fontWeight: 'bold' }}>
                                                    {formatNumberWithComma(calculateCombinedTotal(calculateTotal(), selectedPendingOrder.total_amount))}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Checkout Button */}
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={handleCheckout}
                            disabled={cart.length === 0 || processingOrder}
                            sx={{
                                mt: 1,
                                py: 1.5,
                                fontWeight: 'bold',
                                fontSize: '1rem'
                            }}
                        >
                            {processingOrder ? <CircularProgress size={24} /> : 
                                (selectedPendingOrder ? 'Add to Existing Order' : 'Complete Club Order')}
                        </Button>

                        {/* Print Receipt */}
                        <PrintReceipt
                            orderData={{
                                date: new Date().toISOString(),
                                customerName: formData.customer_id,
                                items: cart,
                                total: calculateTotal(),
                                orderType: 'Club Order',
                                servant: getCategoriesNameById(formData.servant_id, referenceData.employeeNames),
                                counter: getCategoriesNameById(formData.counter_id, referenceData.counterNames),
                                previousOrderNumber: formData.previousOrderNumber
                            }}
                            disabled={cart.length === 0}
                            sx={{ mt: 1 }}
                        />
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

export default ClubPos;